// Deep Space sandbox — /starter-tape/sandbox/deep-space. The free studio for
// the Deep Space path: it opens once Nebula (tape 7) is done, and grows with
// the path — space and tone knobs, chord blocks, the arpeggiator, sus chords
// and sections all appear as their tapes unlock. The working loop is kept as
// a draft, and it can be saved to the Tape Shelf like any tape.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "../lib/utils";
import {
  arpeggiate,
  chordInBar,
  clearTrack,
  diatonicChord,
  ensureTracks,
  makeSections,
  setBarChord,
  setBars,
  setScale,
  setSectionTrack,
} from "../lib/starterTape";
import { getTape, tapeSong } from "../lib/starterTapeCurriculum";
import { isSandboxUnlocked, saveTake } from "../lib/starterTapeProgress";
import ArpPanel from "../components/starterTape/ArpPanel";
import ArrangementGrid from "../components/starterTape/ArrangementGrid";
import ChordStrip from "../components/starterTape/ChordStrip";
import Studio from "../components/starterTape/Studio";
import TapeShell from "../components/starterTape/TapeShell";
import useTapePlayer from "../components/starterTape/useTapePlayer";
import {
  loadDraft,
  loadProgress,
  saveDraft,
  saveProgress,
} from "../components/starterTape/storage";

const DRAFT_ID = "deepSpaceSandbox";

const extraButton =
  "rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800";

const LANES = [
  { id: "pad", instrument: "pad" },
  { id: "bell", instrument: "bell" },
  { id: "bass", instrument: "bass" },
  { id: "drums", instrument: "drums" },
];

const BAR_OPTIONS = [4, 8, 16];

const SECTION_NAMES = ["Intro", "Build", "Drift", "Return"];

// Chord blocks offered, and the tape that unlocks each pair: Am/F always,
// C/G after Gravity, Dm/Em after Transmission — kept in this order.
const CHORD_UNLOCKS = [
  { indexes: [0, 5], unlockedBy: null },
  { indexes: [2, 6], unlockedBy: "gravity" },
  { indexes: [3, 4], unlockedBy: "transmission" },
];

const sandboxChords = (progress) =>
  CHORD_UNLOCKS.filter(
    (c) => !c.unlockedBy || progress.completed[c.unlockedBy]
  ).flatMap((c) => c.indexes);

// The freshest Deep Space take on the shelf that met its tape's must, or
// null when there isn't one yet.
const latestSpaceTake = (progress) =>
  progress.shelf.find((t) => {
    if (!t.met) return false;
    const tape = getTape(t.tapeId);
    return !!tape && tape.side === "deepSpace";
  }) || null;

const startingSong = (progress) => {
  const draft = loadDraft(DRAFT_ID);
  const source = latestSpaceTake(progress);
  const song = draft || (source ? source.song : tapeSong(getTape("gravity")));
  return setScale(ensureTracks(song, LANES), "naturalMinor");
};

// A new take's title: "Deep Space loop", then "(take n)" after, counted
// against every shelf take whose title starts with "Deep Space loop".
const nextSpaceTitle = (progress) => {
  const n = progress.shelf.filter((t) =>
    t.title.startsWith("Deep Space loop")
  ).length;
  return n === 0 ? "Deep Space loop" : `Deep Space loop (take ${n + 1})`;
};

export default function StarterTapeSpaceSandbox() {
  const [progress, setProgress] = useState(loadProgress);
  const [song, setSong] = useState(() => startingSong(progress));
  const [arpPattern, setArpPattern] = useState("upDown");
  const [savedTitle, setSavedTitle] = useState(null);
  const player = useTapePlayer(song);

  useEffect(() => saveDraft(DRAFT_ID, song), [song]);
  useEffect(() => saveProgress(progress), [progress]);

  if (!isSandboxUnlocked(progress, "nebula")) {
    return (
      <TapeShell
        eyebrow="Deep Space"
        theme="deepSpace"
        title="Deep Space sandbox"
        subtitle="No brief. Every Deep Space tool you've unlocked, in A natural minor."
      >
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-6 text-zinc-300">
          <p>The Deep Space sandbox opens when you finish tape 7, Nebula.</p>
          <Link
            to="/starter-tape"
            className="mt-4 inline-block rounded-lg bg-indigo-300 px-4 py-2 font-semibold text-zinc-900 hover:bg-indigo-200"
          >
            Back to the tapes
          </Link>
        </div>
      </TapeShell>
    );
  }

  const edit = (s) => {
    setSong(s);
    setSavedTitle(null);
  };

  const soundControls = [
    progress.completed.nebula ? "space" : null,
    progress.completed.drift ? "tone" : null,
  ].filter(Boolean);

  const chords = sandboxChords(progress);

  const previewChord = (index, variant = null) => {
    if (player.playing) return;
    const chord = diatonicChord(song, index, undefined, variant);
    if (chord) chord.pitches.forEach((p) => player.preview("pad", p));
  };

  const pickChord = (bar, index) => {
    edit(setBarChord(song, "pad", bar, index));
    if (index !== null) previewChord(index);
  };

  // Turn the chord already in `bar` into a sus chord (or back, for null).
  const pickVariant = (bar, variant) => {
    const current = chordInBar(song, "pad", bar);
    if (!current) return;
    edit(setBarChord(song, "pad", bar, current.index, variant));
    previewChord(current.index, variant);
  };

  const saveToShelf = () => {
    const title = nextSpaceTitle(progress);
    setProgress(
      saveTake(progress, {
        id: `take-${Date.now()}`,
        tapeId: null,
        title,
        song,
        met: true,
        savedAt: Date.now(),
      })
    );
    setSavedTitle(title);
  };

  return (
    <TapeShell
      eyebrow="Deep Space"
      theme="deepSpace"
      title="Deep Space sandbox"
      subtitle="No brief. Every Deep Space tool you've unlocked, in A natural minor."
    >
      <Studio
        song={song}
        setSong={edit}
        player={player}
        showTempo
        soundControls={soundControls}
        beforeLanes={
          <>
            {song.sections ? (
              <div className="mb-4">
                <ArrangementGrid
                  song={song}
                  onToggle={(sectionId, trackId, on) =>
                    edit(setSectionTrack(song, sectionId, trackId, on))
                  }
                />
              </div>
            ) : null}
            {chords.length > 0 && (
              <ChordStrip
                song={song}
                trackId="pad"
                chords={chords}
                onPick={pickChord}
                variants={!!progress.completed.weightless}
                onVariant={pickVariant}
              />
            )}
            {progress.completed.satellites && (
              <div className="mb-4">
                <ArpPanel
                  pattern={arpPattern}
                  onPatternChange={setArpPattern}
                  onArpeggiate={() =>
                    edit(arpeggiate(song, "pad", "bell", arpPattern, 1))
                  }
                  disabled={!song.tracks.some((t) => t.id === "bell")}
                />
              </div>
            )}
            {progress.completed.lightYears &&
              (song.sections ? (
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => edit({ ...song, sections: undefined })}
                    className={extraButton}
                  >
                    Remove sections
                  </button>
                </div>
              ) : (
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={() => edit(makeSections(song, SECTION_NAMES))}
                    disabled={song.bars < 4}
                    className={cn(
                      extraButton,
                      "disabled:cursor-not-allowed disabled:opacity-40"
                    )}
                  >
                    Add sections (Intro, Build, Drift, Return)
                  </button>
                </div>
              ))}
          </>
        }
        transportExtras={
          <>
            <div
              className="flex rounded-lg border border-zinc-700 p-0.5"
              role="group"
              aria-label="Loop length"
            >
              {BAR_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => edit(setBars(song, n))}
                  aria-pressed={song.bars === n}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm",
                    song.bars === n
                      ? "bg-zinc-100 text-zinc-900"
                      : "text-zinc-400 hover:bg-zinc-800"
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() =>
                edit(song.tracks.reduce((s, t) => clearTrack(s, t.id), song))
              }
              className={extraButton}
            >
              Clear all
            </button>
            <button
              type="button"
              onClick={saveToShelf}
              className="rounded-lg bg-indigo-300 px-3 py-2 text-sm font-semibold text-zinc-900 hover:bg-indigo-200"
            >
              Save to shelf
            </button>
          </>
        }
      />
      {savedTitle && (
        <p className="mt-4 text-sm text-emerald-300">
          Saved “{savedTitle}” to your Tape Shelf.
        </p>
      )}
    </TapeShell>
  );
}
